import React, { useState, useEffect } from 'react';
import { X, Download, Mail, CheckCircle, Gift, BookOpen } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ExitIntentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    // Check if popup was already shown in this session
    const popupShown = sessionStorage.getItem('exitPopupShown');
    if (popupShown) {
      setHasShown(true);
      return;
    }

    const handleMouseLeave = (e) => {
      // Detect if mouse is leaving toward the top of the viewport
      if (e.clientY <= 10 && !hasShown) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    };

    // Add slight delay before enabling exit detection
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000); // Wait 5 seconds before enabling

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [hasShown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await axios.post(`${API_URL}/api/leads`, {
        email,
        source: 'exit_intent_popup',
        offer: 'gcse_maths_cheat_sheet'
      });
      setIsSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 99998,
          animation: 'fadeIn 0.3s ease'
        }}
        onClick={handleClose}
      />

      {/* Popup */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '500px',
          background: 'white',
          borderRadius: '20px',
          overflow: 'hidden',
          zIndex: 99999,
          animation: 'slideUp 0.4s ease',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)'
        }}
        data-testid="exit-intent-popup"
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(0, 0, 0, 0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1
          }}
          data-testid="exit-popup-close"
        >
          <X size={18} color="#666" />
        </button>

        {!isSubmitted ? (
          <>
            {/* Header with gradient */}
            <div
              style={{
                background: 'linear-gradient(135deg, #C5A059 0%, #A88B4A 100%)',
                padding: '2rem 1.5rem',
                textAlign: 'center',
                position: 'relative'
              }}
            >
              {/* Gift icon */}
              <div
                style={{
                  width: '70px',
                  height: '70px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Gift size={36} color="#E5C575" />
              </div>

              <h2
                style={{
                  color: 'white',
                  fontSize: 'clamp(1.3rem, 4vw, 1.6rem)',
                  fontWeight: 800,
                  marginBottom: '0.5rem',
                  lineHeight: 1.2
                }}
              >
                Wait! Don't Leave Empty-Handed!
              </h2>
              <p
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.95rem'
                }}
              >
                Get your FREE revision resource
              </p>
            </div>

            {/* Content */}
            <div style={{ padding: '1.5rem' }}>
              {/* Offer box */}
              <div
                style={{
                  background: 'linear-gradient(135deg, #FFFCF5 0%, #E5C575 100%)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  marginBottom: '1.5rem',
                  border: '2px solid #C5A059',
                  textAlign: 'center'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <BookOpen size={24} color="#0F172A" />
                  <span style={{ fontWeight: 800, fontSize: '1.1rem', color: '#0F172A' }}>
                    GCSE Maths Revision Cheat Sheet
                  </span>
                </div>
                <p style={{ color: '#0F172A', fontSize: '0.85rem', margin: 0 }}>
                  Essential formulas, tips & exam strategies - all in one page!
                </p>
              </div>

              {/* Benefits */}
              <div style={{ marginBottom: '1.5rem' }}>
                {[
                  'All key formulas in one place',
                  'Exam technique tips from an IGCSE examiner',
                  'Common mistakes to avoid'
                ].map((benefit, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem'
                    }}
                  >
                    <CheckCircle size={18} color="#C5A059" />
                    <span style={{ fontSize: '0.9rem', color: '#374151' }}>{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '1rem' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      border: '2px solid #E5E7EB',
                      borderRadius: '9999px',
                      padding: '0.5rem 1rem',
                      background: '#F9FAFB'
                    }}
                  >
                    <Mail size={20} color="#9CA3AF" style={{ marginRight: '0.75rem' }} />
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      style={{
                        flex: 1,
                        border: 'none',
                        background: 'transparent',
                        fontSize: '1rem',
                        outline: 'none',
                        color: '#374151'
                      }}
                      data-testid="exit-popup-email"
                    />
                  </div>
                  {error && (
                    <p style={{ color: '#0F172A', fontSize: '0.8rem', marginTop: '0.5rem', marginLeft: '1rem' }}>
                      {error}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #C5A059 0%, #A88B4A 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '9999px',
                    padding: '1rem',
                    fontSize: '1rem',
                    fontWeight: 700,
                    cursor: isLoading ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'transform 0.2s ease, opacity 0.2s ease',
                    opacity: isLoading ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading) e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  data-testid="exit-popup-submit"
                >
                  <Download size={20} />
                  {isLoading ? 'Sending...' : 'Get My Free Cheat Sheet'}
                </button>
              </form>

              <p
                style={{
                  textAlign: 'center',
                  color: '#9CA3AF',
                  fontSize: '0.75rem',
                  marginTop: '1rem'
                }}
              >
                No spam, ever. Unsubscribe anytime.
              </p>
            </div>
          </>
        ) : (
          /* Success state */
          <div style={{ padding: '3rem 2rem', textAlign: 'center' }}>
            <div
              style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #C5A059 0%, #A88B4A 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}
            >
              <CheckCircle size={40} color="white" />
            </div>

            <h2
              style={{
                color: '#0F172A',
                fontSize: '1.5rem',
                fontWeight: 800,
                marginBottom: '0.75rem'
              }}
            >
              Check Your Email!
            </h2>
            <p
              style={{
                color: '#6B7280',
                fontSize: '1rem',
                marginBottom: '1.5rem',
                lineHeight: 1.5
              }}
            >
              Your GCSE Maths Cheat Sheet is on its way to <strong>{email}</strong>
            </p>

            <div
              style={{
                background: '#FFFCF5',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '1.5rem',
                border: '1px solid #E5C575'
              }}
            >
              <p style={{ color: '#0F172A', fontSize: '0.9rem', margin: 0 }}>
                💡 <strong>Pro tip:</strong> Check your spam folder if you don't see it in a few minutes!
              </p>
            </div>

            <button
              onClick={handleClose}
              style={{
                background: 'linear-gradient(135deg, #C5A059 0%, #A88B4A 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                padding: '0.875rem 2rem',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
              data-testid="exit-popup-done"
            >
              Continue Browsing
            </button>
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translate(-50%, -45%);
          }
          to { 
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }
      `}</style>
    </>
  );
};

export default ExitIntentPopup;
